/**
 * PARTE 3 — MethodologyDetailPanel
 * Panel expandible debajo de cada metodología seleccionada en InvestigationTab.
 * Soporta: generar (LLM), lectura, edición manual, restaurar IA, info adicional, PDF.
 *
 * Props:
 *  - caseId: id del caso
 *  - methodologyName: nombre legible (ej. "5 Whys", "TapRooT®")
 *  - methodologySlug: slug ya resuelto (ej. "5_whys", "custom_xyz")
 *  - existingDetail: si ya hay detalle generado, pasarlo aquí para hidratar
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "../../../lib/api";
import { Modal } from "../../../components/ui";
import {
  ChevronDown, ChevronUp, FileText, Edit2, RotateCcw,
  Plus, X as XIcon, Loader2, Sparkles, CheckCircle, Save,
} from "lucide-react";

const PRIORITY_COLORS = {
  "Inmediata": "#D50000",
  "Corto Plazo": "#FB8C00",
  "Mediano Plazo": "#1565C0",
};

function priorityBadge(p) {
  const color = PRIORITY_COLORS[p] || "#546E7A";
  return (
    <span
      style={{
        background: color, color: "#FFFFFF",
        padding: "2px 8px", borderRadius: 12, fontSize: 10, fontWeight: 700,
        whiteSpace: "nowrap",
      }}
      data-testid={`mdp-priority-${(p || "").replace(/\s+/g, "_")}`}
    >{p || "—"}</span>
  );
}

function emptyDetail(name, slug) {
  return {
    slug, methodology_name: name, framework: "",
    steps: [], root_causes: [], contributing_factors: [],
    conclusions: "", recommendations: [], methodology_verdict: "",
    additional_info: "", is_edited: false, generated_at: null,
  };
}

export default function MethodologyDetailPanel({
  caseId, methodologyName, methodologySlug, existingDetail,
}) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(existingDetail || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [editing, setEditing] = useState(false);
  const [editBuffer, setEditBuffer] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [infoBuffer, setInfoBuffer] = useState("");
  const [savingInfo, setSavingInfo] = useState(false);

  const [showLongWait, setShowLongWait] = useState(false);

  useEffect(() => {
    setDetail(existingDetail || null);
    setInfoBuffer(existingDetail?.additional_info || "");
  }, [existingDetail]);

  function showToast(msg, ms = 2500) {
    setToast(msg);
    setTimeout(() => setToast(""), ms);
  }

  async function generate() {
    if (!methodologySlug) {
      setError("Sin slug de metodología — no se puede generar el detalle.");
      return;
    }
    setError(""); setLoading(true); setShowLongWait(false);
    // FIX 3c — Mostrar mensaje de espera larga si excede 15s
    const longWaitId = setTimeout(() => setShowLongWait(true), 15000);
    try {
      const r = await api.post(`/investigations/${caseId}/methodology-detail/${encodeURIComponent(methodologySlug)}`);
      setDetail(r.data);
      setInfoBuffer(r.data.additional_info || "");
      showToast("Detalle generado");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Error generando el detalle");
    } finally {
      clearTimeout(longWaitId);
      setShowLongWait(false);
      setLoading(false);
    }
  }

  async function downloadPdf() {
    try {
      const r = await api.post(
        `/investigations/${caseId}/methodology-detail/${encodeURIComponent(methodologySlug)}/pdf`,
        {}, { responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${caseId}_${methodologySlug}_detalle.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 800);
    } catch (e) {
      setError("No se pudo descargar PDF: " + (e?.response?.data?.detail || e.message));
    }
  }

  function startEdit() {
    setEditBuffer(JSON.parse(JSON.stringify(detail)));
    setEditing(true);
  }
  function cancelEdit() {
    setEditBuffer(null);
    setEditing(false);
  }

  async function saveEdit() {
    setSavingEdit(true);
    try {
      const payload = {
        steps: editBuffer.steps || [],
        root_causes: editBuffer.root_causes || [],
        contributing_factors: editBuffer.contributing_factors || [],
        conclusions: editBuffer.conclusions || "",
        recommendations: editBuffer.recommendations || [],
        methodology_verdict: editBuffer.methodology_verdict || "",
      };
      const r = await api.put(
        `/investigations/${caseId}/methodology-detail/${encodeURIComponent(methodologySlug)}/edit`,
        payload,
      );
      setDetail(r.data);
      setEditing(false);
      setEditBuffer(null);
      showToast("Investigación actualizada");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setSavingEdit(false); }
  }

  async function resetToOriginal() {
    try {
      const r = await api.post(
        `/investigations/${caseId}/methodology-detail/${encodeURIComponent(methodologySlug)}/reset`,
      );
      setDetail(r.data);
      setShowResetModal(false);
      showToast("Análisis restaurado al original de IA");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }

  async function saveInfo() {
    setSavingInfo(true);
    try {
      const r = await api.patch(
        `/investigations/${caseId}/methodology-detail/${encodeURIComponent(methodologySlug)}/info`,
        { additional_info: infoBuffer },
      );
      setDetail(r.data);
      showToast("Notas guardadas");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setSavingInfo(false); }
  }

  const buttonLabel = useMemo(() => {
    if (!detail) return { text: "📋 Ver Detalle Paso a Paso", color: "#1565C0" };
    if (detail.is_edited) return { text: "📋 Ver Detalle ✏", color: "#FB8C00" };
    return { text: "📋 Ver Detalle ✓", color: "#00C853" };
  }, [detail]);

  return (
    <div style={{ marginTop: 6, marginBottom: 6 }} data-testid={`mdp-${methodologySlug}`}>
      <button
        className="btn btn-sm"
        onClick={() => setExpanded(!expanded)}
        style={{ background: buttonLabel.color, color: "#FFF", borderColor: buttonLabel.color }}
        data-testid={`mdp-toggle-${methodologySlug}`}
      >
        {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />} {buttonLabel.text}
      </button>

      {expanded && (
        <div className="card mt-2" data-testid={`mdp-panel-${methodologySlug}`}>
          {/* Header */}
          <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
            <div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>
                📋 {methodologyName}
              </div>
              {detail?.framework && (
                <div style={{ fontSize: 11, color: "var(--text-dim)" }}>{detail.framework}</div>
              )}
              {detail?.generated_at && (
                <div style={{ fontSize: 11, color: "var(--text-dim)" }}>
                  Generado: {detail.generated_at}
                </div>
              )}
              {detail?.is_edited && (
                <div style={{ marginTop: 4 }}>
                  <span style={{
                    background: "#FB8C00", color: "#FFF",
                    padding: "2px 8px", borderRadius: 12, fontSize: 10, fontWeight: 700,
                  }} data-testid={`mdp-edited-badge-${methodologySlug}`}>
                    ✏ Editado manualmente
                  </span>
                  <span style={{ fontSize: 11, color: "var(--text-dim)", marginLeft: 6 }}>
                    por {detail.edited_by || "—"} el {detail.edited_at || ""}
                  </span>
                </div>
              )}
            </div>
            <div className="flex gap-1 flex-wrap">
              {detail && !editing && (
                <button className="btn btn-sm" onClick={downloadPdf} data-testid={`mdp-pdf-${methodologySlug}`}>
                  ⬇ PDF
                </button>
              )}
              {detail && !editing && (
                <button className="btn btn-sm btn-primary" onClick={startEdit} data-testid={`mdp-edit-${methodologySlug}`}>
                  <Edit2 size={11}/> Editar Investigación
                </button>
              )}
              {detail?.is_edited && !editing && (
                <button className="btn btn-sm" onClick={() => setShowResetModal(true)} data-testid={`mdp-reset-${methodologySlug}`}>
                  <RotateCcw size={11}/> Restaurar IA
                </button>
              )}
            </div>
          </div>

          {error && (
            <div className="card mb-2" style={{ background: "rgba(213,0,0,0.10)", borderColor: "#D50000", fontSize: 12 }}
                 data-testid={`mdp-error-${methodologySlug}`}>
              {error}
            </div>
          )}
          {toast && (
            <div className="card mb-2" style={{ background: "rgba(0,200,83,0.10)", borderColor: "#00C853", fontSize: 12 }}
                 data-testid={`mdp-toast-${methodologySlug}`}>
              <CheckCircle size={11} style={{ display: "inline", marginRight: 4 }}/> {toast}
            </div>
          )}

          {/* Sin detalle aún */}
          {!detail && !loading && (
            <div className="text-center" style={{ padding: 20 }}>
              <div className="text-dim text-sm mb-2">
                La IA aplicará <b>{methodologyName}</b> paso a paso a este caso.
              </div>
              <button className="btn btn-primary" onClick={generate} data-testid={`mdp-generate-${methodologySlug}`}>
                <Sparkles size={12}/> 🤖 Generar Detalle
              </button>
            </div>
          )}
          {loading && (
            <div className="text-center" style={{ padding: 20 }}>
              <Loader2 size={18} className="spin"/> Aplicando {methodologyName} al caso...
              {showLongWait && (
                <div className="text-xs text-dim mt-2" data-testid={`mdp-longwait-${methodologySlug}`}>
                  El análisis está en proceso, puede tardar hasta 90 segundos
                  dependiendo de la complejidad del caso...
                </div>
              )}
            </div>
          )}

          {/* Modo lectura */}
          {detail && !editing && !loading && (
            <ReadMode detail={detail} slug={methodologySlug} />
          )}

          {/* Modo edición */}
          {detail && editing && (
            <EditMode
              buffer={editBuffer}
              setBuffer={setEditBuffer}
              onCancel={cancelEdit}
              onSave={saveEdit}
              saving={savingEdit}
              slug={methodologySlug}
            />
          )}

          {/* Información Adicional */}
          {detail && !editing && (
            <div className="card mt-3" style={{ background: "var(--bg-2)" }}
                 data-testid={`mdp-info-${methodologySlug}`}>
              <div className="card-title">📝 INFORMACIÓN ADICIONAL DEL EQUIPO</div>
              <div className="text-xs text-dim mb-2">
                Escribe aquí información relevante que no esté en los documentos:
                observaciones de campo, testimonios, datos técnicos adicionales,
                contexto operacional...
              </div>
              <textarea
                className="textarea" rows={4}
                value={infoBuffer}
                onChange={(e) => setInfoBuffer(e.target.value)}
                data-testid={`mdp-info-textarea-${methodologySlug}`}
              />
              <div className="flex items-center justify-between mt-2">
                <div className="text-xs text-dim">
                  {detail.additional_info_updated_at && (
                    <>Última actualización: {detail.additional_info_updated_at}
                      {detail.additional_info_updated_by && ` por ${detail.additional_info_updated_by}`}</>
                  )}
                </div>
                <button
                  className="btn btn-sm btn-primary"
                  disabled={savingInfo}
                  onClick={saveInfo}
                  data-testid={`mdp-info-save-${methodologySlug}`}
                >
                  {savingInfo ? <Loader2 size={11} className="spin"/> : <Save size={11}/>} Guardar Notas
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <Modal open={showResetModal} onClose={() => setShowResetModal(false)} title="Restaurar análisis original">
        <div className="text-sm mb-3">
          ¿Restaurar el análisis original de IA? Los cambios manuales se perderán.
        </div>
        <div className="flex gap-2 justify-end">
          <button className="btn" onClick={() => setShowResetModal(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={resetToOriginal} data-testid={`mdp-reset-confirm-${methodologySlug}`}>
            Restaurar
          </button>
        </div>
      </Modal>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MODO LECTURA
// ─────────────────────────────────────────────────────────────────
function ReadMode({ detail, slug }) {
  return (
    <div data-testid={`mdp-read-${slug}`}>
      {(detail.steps || []).map((step) => (
        <details key={step.step_number} open style={{ marginBottom: 8 }}
                 data-testid={`mdp-step-${slug}-${step.step_number}`}>
          <summary style={{ cursor: "pointer", padding: 6, fontWeight: 600 }}>
            PASO {step.step_number} — {step.step_name}
          </summary>
          <div style={{ padding: "6px 12px" }}>
            {step.analysis && <p style={{ fontSize: 13 }}>{step.analysis}</p>}
            {(step.findings || []).length > 0 && (
              <div className="mt-1">
                <b style={{ fontSize: 12 }}>📌 Hallazgos:</b>
                <ul style={{ fontSize: 12, paddingLeft: 16 }}>
                  {step.findings.map((f, i) => (
                    <li key={`f-${step.step_number}-${i}`}>{f}</li>
                  ))}
                </ul>
              </div>
            )}
            {(step.evidence_references || []).length > 0 && (
              <div className="mt-1">
                <b style={{ fontSize: 12 }}>📎 Referencias a evidencias:</b>
                <ul style={{ fontSize: 12, paddingLeft: 16 }}>
                  {step.evidence_references.map((r, i) => (
                    <li key={`er-${step.step_number}-${i}`}>{r}</li>
                  ))}
                </ul>
              </div>
            )}
            {(step.sub_steps || []).length > 0 && (
              <div className="mt-1" style={{ paddingLeft: 12 }}>
                {step.sub_steps.map((s, i) => (
                  <div key={`ss-${step.step_number}-${i}`} style={{ fontSize: 12 }}>
                    <b>{s.name}:</b> {s.content}
                  </div>
                ))}
              </div>
            )}
          </div>
        </details>
      ))}

      {(detail.root_causes || []).length > 0 && (
        <div className="card mt-3" data-testid={`mdp-root-causes-${slug}`}>
          <div className="card-title">Causas Raíz</div>
          <ul style={{ paddingLeft: 18 }}>
            {detail.root_causes.map((c, i) => (
              <li key={`rc-${i}`} style={{ color: "#D50000", fontSize: 13 }}>{c}</li>
            ))}
          </ul>
        </div>
      )}

      {(detail.contributing_factors || []).length > 0 && (
        <div className="card mt-2" data-testid={`mdp-contrib-${slug}`}>
          <div className="card-title">Factores Contribuyentes</div>
          <ul style={{ paddingLeft: 18 }}>
            {detail.contributing_factors.map((f, i) => (
              <li key={`cf-${i}`} style={{ color: "#FB8C00", fontSize: 13 }}>{f}</li>
            ))}
          </ul>
        </div>
      )}

      {detail.conclusions && (
        <div className="card mt-2" style={{ background: "rgba(21,101,192,0.10)", borderColor: "#1565C0" }}
             data-testid={`mdp-conclusions-${slug}`}>
          <div className="card-title">Conclusiones</div>
          <p style={{ fontSize: 13 }}>{detail.conclusions}</p>
        </div>
      )}

      {(detail.recommendations || []).length > 0 && (
        <div className="card mt-2" data-testid={`mdp-recs-${slug}`}>
          <div className="card-title">Recomendaciones</div>
          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--bg-2)" }}>
                <th style={{ padding: 6, textAlign: "left" }}>Acción</th>
                <th style={{ padding: 6, textAlign: "left" }}>Prioridad</th>
                <th style={{ padding: 6, textAlign: "left" }}>Responsable</th>
                <th style={{ padding: 6, textAlign: "left" }}>Justificación</th>
              </tr>
            </thead>
            <tbody>
              {detail.recommendations.map((r, i) => (
                <tr key={`rec-${i}`} style={{ borderTop: "1px solid var(--border-2)" }}>
                  <td style={{ padding: 6 }}>{r.action}</td>
                  <td style={{ padding: 6 }}>{priorityBadge(r.priority)}</td>
                  <td style={{ padding: 6 }}>{r.responsible}</td>
                  <td style={{ padding: 6 }}>{r.rationale}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail.methodology_verdict && (
        <div className="mt-2" style={{
          borderLeft: "3px solid #546E7A", paddingLeft: 10,
          fontStyle: "italic", fontSize: 13, color: "var(--text-dim)",
        }} data-testid={`mdp-verdict-${slug}`}>
          {detail.methodology_verdict}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MODO EDICIÓN
// ─────────────────────────────────────────────────────────────────
function EditableList({ items, onChange, placeholder, testIdPrefix }) {
  function set(i, v) {
    const next = [...items];
    next[i] = v;
    onChange(next);
  }
  function remove(i) {
    onChange(items.filter((_, idx) => idx !== i));
  }
  function add() {
    onChange([...items, ""]);
  }
  return (
    <div>
      {items.map((item, i) => (
        <div key={`${testIdPrefix}-${i}`} className="flex gap-1 mt-1">
          <input
            className="input" value={item || ""}
            placeholder={placeholder}
            onChange={(e) => set(i, e.target.value)}
            data-testid={`${testIdPrefix}-${i}`}
          />
          <button className="btn btn-sm" onClick={() => remove(i)}
                  data-testid={`${testIdPrefix}-remove-${i}`}>
            <XIcon size={11}/>
          </button>
        </div>
      ))}
      <button className="btn btn-sm mt-2" onClick={add} data-testid={`${testIdPrefix}-add`}>
        <Plus size={11}/> Agregar
      </button>
    </div>
  );
}

function EditMode({ buffer, setBuffer, onCancel, onSave, saving, slug }) {
  function patchStep(i, k, v) {
    const next = { ...buffer };
    const steps = [...(next.steps || [])];
    steps[i] = { ...steps[i], [k]: v };
    next.steps = steps;
    setBuffer(next);
  }
  function patchRec(i, k, v) {
    const next = { ...buffer };
    const recs = [...(next.recommendations || [])];
    recs[i] = { ...recs[i], [k]: v };
    next.recommendations = recs;
    setBuffer(next);
  }
  function addRec() {
    setBuffer({
      ...buffer,
      recommendations: [...(buffer.recommendations || []),
        { action: "", priority: "Corto Plazo", responsible: "", rationale: "" }],
    });
  }
  function removeRec(i) {
    setBuffer({
      ...buffer,
      recommendations: (buffer.recommendations || []).filter((_, idx) => idx !== i),
    });
  }

  return (
    <div data-testid={`mdp-edit-${slug}`}>
      {(buffer.steps || []).map((s, i) => (
        <div key={`edit-step-${i}`} className="card mt-2">
          <div className="text-xs text-dim mb-1">PASO {s.step_number || i + 1}</div>
          <input
            className="input" value={s.step_name || ""}
            onChange={(e) => patchStep(i, "step_name", e.target.value)}
            placeholder="Nombre del paso"
            data-testid={`mdp-edit-step-name-${slug}-${i}`}
          />
          <textarea
            className="textarea mt-1" rows={3} value={s.analysis || ""}
            onChange={(e) => patchStep(i, "analysis", e.target.value)}
            placeholder="Análisis"
            data-testid={`mdp-edit-step-analysis-${slug}-${i}`}
          />
          <div className="text-xs mt-2"><b>Hallazgos</b></div>
          <EditableList
            items={s.findings || []}
            onChange={(v) => patchStep(i, "findings", v)}
            placeholder="Hallazgo..."
            testIdPrefix={`mdp-edit-step-finding-${slug}-${i}`}
          />
          <div className="text-xs mt-2"><b>Referencias a evidencias</b></div>
          <EditableList
            items={s.evidence_references || []}
            onChange={(v) => patchStep(i, "evidence_references", v)}
            placeholder="Referencia..."
            testIdPrefix={`mdp-edit-step-ref-${slug}-${i}`}
          />
        </div>
      ))}

      <div className="card mt-2">
        <div className="card-title">Causas Raíz</div>
        <EditableList
          items={buffer.root_causes || []}
          onChange={(v) => setBuffer({ ...buffer, root_causes: v })}
          placeholder="Causa raíz..."
          testIdPrefix={`mdp-edit-rc-${slug}`}
        />
      </div>

      <div className="card mt-2">
        <div className="card-title">Factores Contribuyentes</div>
        <EditableList
          items={buffer.contributing_factors || []}
          onChange={(v) => setBuffer({ ...buffer, contributing_factors: v })}
          placeholder="Factor contribuyente..."
          testIdPrefix={`mdp-edit-cf-${slug}`}
        />
      </div>

      <div className="card mt-2">
        <div className="card-title">Conclusiones</div>
        <textarea
          className="textarea" rows={5}
          value={buffer.conclusions || ""}
          onChange={(e) => setBuffer({ ...buffer, conclusions: e.target.value })}
          data-testid={`mdp-edit-conclusions-${slug}`}
        />
      </div>

      <div className="card mt-2">
        <div className="card-title">Recomendaciones</div>
        {(buffer.recommendations || []).map((r, i) => (
          <div key={`edit-rec-${i}`} className="card mt-2" style={{ background: "var(--bg-2)" }}>
            <div className="flex justify-between mb-1">
              <div className="text-xs text-dim">Recomendación #{i + 1}</div>
              <button className="btn btn-sm" onClick={() => removeRec(i)}
                      data-testid={`mdp-edit-rec-remove-${slug}-${i}`}>
                <XIcon size={11}/>
              </button>
            </div>
            <input
              className="input" placeholder="Acción"
              value={r.action || ""}
              onChange={(e) => patchRec(i, "action", e.target.value)}
              data-testid={`mdp-edit-rec-action-${slug}-${i}`}
            />
            <div className="flex gap-1 mt-1">
              <select
                className="input" value={r.priority || "Corto Plazo"}
                onChange={(e) => patchRec(i, "priority", e.target.value)}
                data-testid={`mdp-edit-rec-priority-${slug}-${i}`}
              >
                <option>Inmediata</option>
                <option>Corto Plazo</option>
                <option>Mediano Plazo</option>
              </select>
              <input
                className="input" placeholder="Responsable"
                value={r.responsible || ""}
                onChange={(e) => patchRec(i, "responsible", e.target.value)}
                data-testid={`mdp-edit-rec-resp-${slug}-${i}`}
              />
            </div>
            <textarea
              className="textarea mt-1" rows={2} placeholder="Justificación"
              value={r.rationale || ""}
              onChange={(e) => patchRec(i, "rationale", e.target.value)}
              data-testid={`mdp-edit-rec-rat-${slug}-${i}`}
            />
          </div>
        ))}
        <button className="btn btn-sm mt-2" onClick={addRec} data-testid={`mdp-edit-rec-add-${slug}`}>
          <Plus size={11}/> Agregar recomendación
        </button>
      </div>

      <div className="card mt-2">
        <div className="card-title">Veredicto de la Metodología</div>
        <textarea
          className="textarea" rows={2}
          value={buffer.methodology_verdict || ""}
          onChange={(e) => setBuffer({ ...buffer, methodology_verdict: e.target.value })}
          data-testid={`mdp-edit-verdict-${slug}`}
        />
      </div>

      {/* Sticky bar */}
      <div style={{
        position: "sticky", bottom: 0, background: "var(--bg-1)",
        padding: 8, borderTop: "1px solid var(--border)", marginTop: 12,
        display: "flex", justifyContent: "flex-end", gap: 8, zIndex: 5,
      }}>
        <button className="btn" disabled={saving} onClick={onCancel}
                data-testid={`mdp-edit-cancel-${slug}`}>
          Cancelar
        </button>
        <button className="btn btn-primary" disabled={saving} onClick={onSave}
                data-testid={`mdp-edit-save-${slug}`}>
          {saving ? <Loader2 size={11} className="spin"/> : <Save size={11}/>} 💾 Guardar Cambios
        </button>
      </div>
    </div>
  );
}
